import {
  readEmbeddedProductionApiFromExpoExtra,
  resolveProductionApiBaseUrl,
} from '../helpers/productionApiUrl.helper';
import { isApiDebugEnabled } from './debug';
import { logger } from '../src/helpers/logger.helper';
import {
  INJECTED_BUNDLE_ENV,
  INJECTED_PRODUCTION_API_URL,
} from '../src/constants/api-config.generated';

/**
 * API Configuration
 * Centralized configuration for all API endpoints
 * When EXPO_PUBLIC_API_URL_DEV is set (e.g. http://YOUR_IP:3001/api/v1), it is used for all API calls
 * so the app works when opened from another device (e.g. phone at http://YOUR_IP:8081).
 *
 * Vercel Preview: set EXPO_PUBLIC_API_URL_PREVIEW (Preview env) to your staging API base. At runtime on
 * *.vercel.app, that value is used. `scripts/vercel-build.sh` also maps it to REACT_APP_API_URL_PROD
 * when VERCEL_ENV=preview for build-time inlining.
 */

const injectedProductionApiUrl = String(INJECTED_PRODUCTION_API_URL ?? '').trim();
const embeddedProductionApiUrl = readEmbeddedProductionApiFromExpoExtra();
const devApiUrl = (
  process.env.EXPO_PUBLIC_API_URL_DEV || process.env.REACT_APP_API_URL_DEV || ''
).trim();

// API Base URLs (EXPO_PUBLIC_API_URL_DEV takes precedence when set)
const API_URLS = {
  development: process.env.EXPO_PUBLIC_API_URL_DEV || process.env.REACT_APP_API_URL_DEV || 'http://localhost:3001/api/v1',
  production: injectedProductionApiUrl
    || embeddedProductionApiUrl
    || resolveProductionApiBaseUrl(
      process.env as Record<string, string | undefined>,
      typeof window !== 'undefined' && window.location != null ? window : undefined
    ),
};

// Injected literals + embedded app.config extra win over Metro env (Xcode may re-bundle with dev .env).
// EXPO_PUBLIC_API_URL_DEV always wins for local web/backend dev (embedded prod URL is native-only).
const useProduction =
  !devApiUrl
  && (
    INJECTED_BUNDLE_ENV === 'production'
    || process.env.EXPO_PUBLIC_ENV === 'production'
    || embeddedProductionApiUrl.length > 0
  );
export const API_URL = useProduction ? API_URLS.production : API_URLS.development;

if (typeof window !== 'undefined' && window.location != null && useProduction && API_URL.includes('api.zporti.com')) {
  const host = window.location.hostname.toLowerCase();
  if (host === 'vercel.app' || host.endsWith('.vercel.app')) {
    logger.warn(
      'Vercel Preview is using the production API (expect CORS errors). In Vercel → Environment Variables, add EXPO_PUBLIC_API_URL_PREVIEW for the Preview environment only, set to your Railway staging API base (include /api/v1), then redeploy. Ensure this branch includes constants/api.ts + helpers/productionApiUrl.helper.ts.'
    );
  }
}

/** Base URL for Socket.io (same origin as API, without /api/v1 path) */
export const SOCKET_IO_URL = API_URL.replace(/\/api\/v1\/?$/, '') || API_URL.split('/api')[0];

// Debug logging for environment detection (opt-in: set EXPO_PUBLIC_DEBUG_API=true)
if (isApiDebugEnabled && typeof window !== 'undefined') {
  logger.debug('API Environment Debug', {
    EXPO_PUBLIC_ENV: process.env.EXPO_PUBLIC_ENV,
    REACT_APP_ENV: process.env.REACT_APP_ENV,
    NODE_ENV: process.env.NODE_ENV,
    useProduction,
    API_URL,
    EXPO_PUBLIC_API_URL_PREVIEW_set: Boolean(
      (process.env.EXPO_PUBLIC_API_URL_PREVIEW || '').trim()
    ),
    timestamp: new Date().toISOString(),
  });
}

// API Endpoints
export const API_ENDPOINTS = {
  // Public app config (read pre-auth, e.g. the update gate)
  appConfig: {
    version: '/app-config/version',
    announcements: '/app-config/announcements',
  },

  // Home landing page (specs/home-page)
  home: {
    slateToday: '/home/slate/today',
    quests: '/home/quests',
    questsChestSeen: '/home/quests/chest-seen',
  },

  // Authentication
  auth: {
    register: '/auth/register',
    login: '/auth/login',
    refresh: '/auth/refresh',
    logout: '/auth/logout'
  },

  // Users
  users: {
    list: '/users',
    profile: '/users/profile',
    usernameAvailable: '/users/username-available',
    dailyClaim: '/users/me/daily-claim',
    xp: '/users/me/xp',
    xpMilestones: '/users/me/xp/milestones',
    xpBatch: '/users/xp/batch',
    redeemCoupon: '/users/me/redeem-coupon',
    lastSeenResults: '/users/me/last-seen-results',
    anonymousLogoutDelete: '/users/me/anonymous-logout-delete',
    accountDeletionRequest: '/users/me/account-deletion-request',
    accountDeletionStatus: '/users/me/account-deletion-status',
    privacyPreferences: '/users/me/privacy-preferences',
    dateOfBirth: '/users/me/date-of-birth',
    pushToken: '/users/me/push-token',
    dataExport: '/users/me/data-export',
    dataExportResetLimit: '/users/me/data-export/reset-limit',
    friends: '/users/friends',
    friendSuggestions: '/users/friends/suggestions',
    pickHistory: '/users/pick-history',
    friendStats: '/users/friends/:friendId/stats'
  },

  // Matches
  matches: {
    all: '/matches',
    byDate: '/matches/date',
    dates: '/matches/dates',
    byLeague: '/matches/league',
    leagues: '/matches/leagues',
    results: '/matches/results',
    odds: '/matches/odds',
    match: (id: string) => `/matches/${encodeURIComponent(id)}`,
    h2h: (id: string) => `/matches/${encodeURIComponent(id)}/h2h`,
    lineups: (id: string) => `/matches/${encodeURIComponent(id)}/lineups`,
    events: (id: string) => `/matches/${encodeURIComponent(id)}/events`,
    insights: (id: string) => `/matches/${encodeURIComponent(id)}/insights`,
  },

  predictions: {
    list: '/predictions',
    batch: '/predictions/batch'
  },

  // Challenges
  challenges: {
    create: '/challenges',
    list: '/challenges',
    details: '/challenges',
    update: '/challenges',
    delete: '/challenges',
    reset: '/challenges/reset',
    invitations: '/challenges/invitations',
    accept: '/challenges',
    decline: '/challenges'
  },

  // Leagues
  leagues: {
    all: '/leagues',
    teams: '/leagues/teams',
    standings: '/leagues/standings',
    schedule: '/leagues/schedule',
    byIdStandings: (leagueId: string) => `/leagues/${encodeURIComponent(leagueId)}/standings`,
    byIdSchedule: (leagueId: string) => `/leagues/${encodeURIComponent(leagueId)}/schedule`,
    byIdAnalytics: (leagueId: string) => `/leagues/${encodeURIComponent(leagueId)}/analytics`,
  },

  // Invitations
  invitations: {
    send: '/invitations/send',
    getByToken: '/invitations',
    accept: '/invitations',
    counts: '/invitations/counts',
  },

  // Contact
  contact: {
    submit: '/contact'
  },

  // Support (authenticated in-app form)
  support: {
    submit: '/support',
  },

  // B2B organization accounts (branding, members)
  accounts: {
    user: '/accounts/user',
    root: '/accounts',
    join: '/accounts/join',
    leave: '/accounts/leave',
    contactLead: '/accounts/contact-lead',
    bySlugPublic: '/accounts/by-slug/:slug/public',
    byJoinCodePublic: '/accounts/by-join-code/:code/public',
    branding: (accountId: string) => `/accounts/${accountId}/branding`,
    account: (accountId: string) => `/accounts/${accountId}`,
    logo: (accountId: string) => `/accounts/${accountId}/logo`,
    logoAtIndex: (accountId: string, index: number) =>
      `/accounts/${accountId}/logo/${index}`,
    logoDownloadAtIndex: (accountId: string, index: number) =>
      `/accounts/${accountId}/logo/${index}/download`,
    banner: (accountId: string) => `/accounts/${accountId}/banner`,
    monkey: (accountId: string) => `/accounts/${accountId}/monkey`,
    avatars: (accountId: string) => `/accounts/${accountId}/avatars`,
    avatarEnabled: (accountId: string, avatarId: string) =>
      `/accounts/${accountId}/avatars/${encodeURIComponent(avatarId)}/enabled`,
    members: (accountId: string) => `/accounts/${accountId}/members`,
    member: (accountId: string, memberUserId: string) =>
      `/accounts/${accountId}/members/${encodeURIComponent(memberUserId)}`,
    membersBulkRemove: (accountId: string) => `/accounts/${accountId}/members/bulk-remove`,
    requestFullyOpen: (accountId: string) => `/accounts/${accountId}/fully-open-request`,
    cancelFullyOpenRequest: (accountId: string) =>
      `/accounts/${accountId}/fully-open-request/cancel`,
    /** Update or clear `allowedEmailDomain` on a B2B org (SSO domain restriction). */
    ssoSettings: (accountId: string) => `/accounts/${accountId}/sso-settings`,
    /** Public org lookup by slug — used by the company sign-in flow before the user authenticates. */
    publicBySlug: (slug: string) => `/accounts/public/${encodeURIComponent(slug)}`,
  },

  /** Stripe B2B org billing (web checkout + customer portal). */
  billing: {
    b2bCheckoutSession: (accountId: string) =>
      `/billing/b2b/accounts/${accountId}/checkout-session`,
    /** Reconcile org plan from Stripe after checkout return (mirrors consumer `sync-checkout-session`). */
    b2bSyncCheckoutSession: (accountId: string) =>
      `/billing/b2b/accounts/${accountId}/sync-checkout-session`,
    b2bPortalSession: (accountId: string) =>
      `/billing/b2b/accounts/${accountId}/portal-session`,
    /** Individual Pro (no org). */
    consumerProCheckoutSession: '/billing/consumer/pro/checkout-session',
    consumerProPortalSession: '/billing/consumer/pro/portal-session',
    /** In-app manage actions (no portal redirect). */
    consumerProCancel: '/billing/consumer/pro/cancel',
    consumerProResume: '/billing/consumer/pro/resume',
    consumerProSwitchInterval: '/billing/consumer/pro/switch-interval',
    /** Pull latest consumer subscription from Stripe (after portal / to avoid webhook delay). */
    consumerProSyncSubscription: '/billing/consumer/pro/sync-subscription',
    /** Reconcile DB from a completed Stripe Checkout Session (no webhook required). */
    consumerProSyncCheckoutSession: '/billing/consumer/pro/sync-checkout-session',
    /** Create Stripe Checkout Session for a coin bundle (web). */
    coinsCheckoutSession: '/billing/consumer/coins/checkout-session',
    /** Reconcile coin balance after Stripe checkout return (web). */
    coinsSyncCheckoutSession: '/billing/consumer/coins/sync-checkout-session',
    /** Reconcile coin balance after a RevenueCat native purchase (authenticated; credits the logged-in user). */
    coinsRevenueCatSync: '/billing/consumer/coins/revenuecat-sync',
    /** Create Stripe Embedded Checkout Session for a coin bundle (web, no redirect). */
    coinsEmbeddedCheckoutSession: '/billing/consumer/coins/embedded-checkout-session',
    /** Create Stripe Embedded Checkout Session for a new consumer Pro subscription (web, no redirect). */
    consumerProEmbeddedCheckoutSession: '/billing/consumer/pro/embedded-checkout-session',
    /** Verify and sync consumer Pro entitlement after a RevenueCat native purchase. */
    consumerProRevenueCatSync: '/billing/consumer/pro/revenuecat-sync',
  },

  // Zporti internal admin
  zportiAdmin: {
    access: '/admin/b2b/access',
    user: '/admin/b2b/user',
    accounts: '/admin/b2b/accounts',
    accountDetail: (id: string) => `/admin/b2b/accounts/${id}`,
    accountDelete: (id: string) => `/admin/b2b/accounts/${id}`,
    accountMembers: (id: string) => `/admin/b2b/accounts/${id}/members`,
    accountPatch: (id: string) => `/admin/b2b/accounts/${id}`,
    regenerateJoinCode: (id: string) => `/admin/b2b/accounts/${id}/regenerate-join-code`,
    generateBranding: (id: string) => `/admin/b2b/accounts/${id}/generate-branding`,
    stats: '/admin/b2b/stats',
    leads: '/admin/b2b/leads',
    leadPatch: (id: string) => `/admin/b2b/leads/${id}`,
    promote: '/admin/b2b/promote',
    impersonate: '/admin/b2b/impersonate',
    impersonateEnd: '/admin/b2b/impersonate/end',
    promoCoupons: '/admin/b2b/promo-coupons',
  },

  // Groups
  groups: {
    list: '/groups',
    details: '/groups',
    members: '/groups/:id/members',
    challenges: '/groups/:id/challenges',
  },

  // Deep Links (unified invite/join system)
  deepLinks: {
    create: '/deep-links/create',
    resolve: '/deep-links/resolve',
    process: '/deep-links/process'
  },

  // Daily games (Warmup: Soccer Wordle, etc.) — generic, keyed by gameKey
  games: {
    today: (gameKey: string) => `/games/${encodeURIComponent(gameKey)}/today`,
    guess: (gameKey: string) => `/games/${encodeURIComponent(gameKey)}/guess`,
    // Endless round flow (Higher or Lower)
    roundStart: (gameKey: string) => `/games/${encodeURIComponent(gameKey)}/round/start`,
    roundGuess: (gameKey: string) => `/games/${encodeURIComponent(gameKey)}/round/guess`,
    // Autocomplete search (Footballer Quiz)
    roundSearch: (gameKey: string) => `/games/${encodeURIComponent(gameKey)}/round/search`,
    // Global all-time best leaderboard (score-based games only)
    leaderboard: (gameKey: string) => `/games/${encodeURIComponent(gameKey)}/leaderboard`,
    // Finite campaign flow (Wall Breaker / slingshot)
    // Multi-world campaign flow (Monkey Worlds) — own path segment; `level/*` is guarded to SLINGSHOT
    worldStart: (gameKey: string) => `/games/${encodeURIComponent(gameKey)}/world/start`,
    worldClear: (gameKey: string) => `/games/${encodeURIComponent(gameKey)}/world/clear`,
    // Level-campaign flow (Match Sport) — own path segment; level 0 = daily challenge
    matchStart: (gameKey: string) => `/games/${encodeURIComponent(gameKey)}/match/start`,
    matchClear: (gameKey: string) => `/games/${encodeURIComponent(gameKey)}/match/clear`,
    levelStart: (gameKey: string) => `/games/${encodeURIComponent(gameKey)}/level/start`,
    levelClear: (gameKey: string) => `/games/${encodeURIComponent(gameKey)}/level/clear`,
    // Endless run flow (Striker Run, Blocky Striker)
    runStart: (gameKey: string) => `/games/${encodeURIComponent(gameKey)}/run/start`,
    runEnd: (gameKey: string) => `/games/${encodeURIComponent(gameKey)}/run/end`,
    // Sudden-death duel flow (Keeper Duel)
    duelStart: (gameKey: string) => `/games/${encodeURIComponent(gameKey)}/duel/start`,
    duelFinish: (gameKey: string) => `/games/${encodeURIComponent(gameKey)}/duel/finish`,
    // Multi-level maze run flow (MazeStriker) — own path segment so it doesn't collide with `run/*`
    mazeStart: (gameKey: string) => `/games/${encodeURIComponent(gameKey)}/maze/start`,
    mazeEnd: (gameKey: string) => `/games/${encodeURIComponent(gameKey)}/maze/end`,
  },

  // Competitions (World Cup / tournament feature)
  competitions: {
    /** Discovery list for the competitions hub (multi-competition-core). */
    list: '/competitions',
    matches: '/competitions/:competitionId/matches',
    staticMatches: '/competitions/:competitionId/matches/static',
    userMatchesOverlay: '/competitions/:competitionId/matches/me',
    pick: '/competitions/:competitionId/picks/:matchId',
    revealMonkeyPicks: '/competitions/:competitionId/picks/reveal-monkey',
    futures: '/competitions/:competitionId/futures',
    goldenBall: '/competitions/:competitionId/golden-ball/:stageKey',
    groups: '/competitions/:competitionId/groups',
    groupDetail: '/competitions/:competitionId/groups/:groupId',
    groupJoin: '/competitions/:competitionId/groups/join',
    groupMembers: '/competitions/:competitionId/groups/:groupId/members',
    leaderboard: '/competitions/:competitionId/groups/:groupId/leaderboard',
    memberProfile: '/competitions/:competitionId/groups/:groupId/members/:memberId/profile',
    participantProfile: '/competitions/:competitionId/participants/:memberId/profile',
    settle: '/competitions/:competitionId/matches/:matchId',
    pickStats: '/competitions/:competitionId/matches/:matchId/pick-stats',
    groupByInviteCodePublic: '/competitions/:competitionId/groups/by-invite-code/:code/public',
    /** Concrete-path builder for the golden-ball PUT endpoint. */
    goldenBallPath: (competitionId: string, stageKey: string) =>
      `/competitions/${encodeURIComponent(competitionId)}/golden-ball/${encodeURIComponent(stageKey)}`,
    /** Concrete-path builder for the "autofill picks" (reveal monkey picks) POST endpoint. */
    revealMonkeyPicksPath: (competitionId: string) =>
      `/competitions/${encodeURIComponent(competitionId)}/picks/reveal-monkey`,
    /** Concrete-path builder for the futures PUT endpoint. */
    futuresPath: (competitionId: string) =>
      `/competitions/${encodeURIComponent(competitionId)}/futures`,
    /** Concrete-path builder for the futures stats GET endpoint. */
    futuresStatsPath: (competitionId: string) =>
      `/competitions/${encodeURIComponent(competitionId)}/futures-stats`,
  },

} as const;

// Helper function to build full API URLs
export const buildApiUrl = (endpoint: string): string => {
  return `${API_URL}${endpoint}`;
};

// Helper function to build API endpoint URLs
export const buildEndpointUrl = (category: keyof typeof API_ENDPOINTS, endpoint: string): string => {
  const baseEndpoint = API_ENDPOINTS[category][endpoint as keyof typeof API_ENDPOINTS[typeof category]];
  return buildApiUrl(baseEndpoint);
};

// Export environment info for debugging
export const ENV_INFO = {
  useProduction,
  currentApiUrl: API_URL,
  nodeEnv: process.env.NODE_ENV,
  REACT_APP_ENV: process.env.REACT_APP_ENV
};
